import { useRef, useState, type FormEvent } from "react";
import { Banner, Button, Field, Input, Modal, TextArea, useToast } from "@/shared/rd";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  capturePrivateSession,
  isPrivateSessionCurrent,
} from "@/lib/api/private-session";
import type { StoreActivityCountKey, StoreActivityCounts } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useDeleteStoreMutation } from "@/modules/stores/services/storeControls";

/** The server's own floor (FORCE_DELETE_MIN_REASON_LENGTH). */
export const FORCE_DELETE_MIN_REASON_LENGTH = 10;

/** The order the erased records are listed in. */
const COUNT_ORDER: StoreActivityCountKey[] = [
  "orders",
  "restaurantBills",
  "billPayments",
  "onlinePayments",
  "moneyMovements",
  "shifts",
  "shiftCashMovements",
  "driverSettlements",
  "subscriptionPayments",
  "storeCharges",
  "storeCustomers",
  "products",
];

type Props = {
  open: boolean;
  onClose: () => void;
  storeId: string;
  storeSlug: string;
  /** What the delete erases, from the controls read. */
  counts: StoreActivityCounts;
  /** Called after the store is gone and its toast is showing (the page navigates away). */
  onDeleted: () => void;
};

/**
 * Force delete (founder request 2026-10-25): erases a DEACTIVATED store with
 * all its activity. Lists what goes, warns in red that it cannot be
 * recovered, and stays disabled until the web address is typed exactly AND a
 * reason of at least 10 characters is written. The server checks both again.
 */
export function StoreForceDeleteModal({ open, onClose, storeId, storeSlug, counts, onDeleted }: Props) {
  const t = useTranslations();
  const lifetime = useRef(capturePrivateSession()).current;
  const toast = useToast();
  const mutation = useDeleteStoreMutation({ force: true });
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const matches = typed.trim().toLowerCase() === storeSlug;
  const reasonOk = reason.trim().length >= FORCE_DELETE_MIN_REASON_LENGTH;
  const erased = COUNT_ORDER.filter((key) => counts[key] > 0);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!matches || !reasonOk) return;
    setError(null);
    try {
      await mutation.mutateAsync({
        storeId,
        confirmSlug: typed.trim(),
        reason: reason.trim(),
      });
      if (!isPrivateSessionCurrent(lifetime)) return;
      toast.show({ message: t(keys.storeControls.forceDeleted) });
      onDeleted();
    } catch (caught) {
      if (!isPrivateSessionCurrent(lifetime)) return;
      if (caught instanceof ApiJsonClientError) {
        if (caught.code === "STORE_FORCE_DELETE_REQUIRES_DEACTIVATION") {
          setError(t(keys.storeControls.forceDeleteNeedsDeactivation));
          return;
        }
        if (caught.code === "STORE_FORCE_DELETE_CUSTOM_DOMAIN") {
          setError(t(keys.storeControls.forceDeleteNeedsDomainRemoval));
          return;
        }
        if (caught.code === "STORE_FORCE_DELETE_REASON_REQUIRED") {
          setError(t(keys.storeControls.forceDeleteReasonTooShort));
          return;
        }
        if (caught.code === "STORE_DELETE_CONFIRMATION_MISMATCH") {
          setError(t(keys.storeControls.deleteConfirmMismatch));
          return;
        }
      }
      setError(
        caught instanceof ApiJsonClientError && caught.message
          ? caught.message
          : t(keys.errors.generic),
      );
    }
  };

  const formId = `store-force-delete-${storeId}`;

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? () => undefined : onClose}
      title={t(keys.storeControls.forceDeleteTitle)}
      actions={
        <>
          <Button variant="ghost" muted onClick={onClose} disabled={mutation.isPending}>
            {t(keys.common.cancel)}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="danger"
            disabled={!matches || !reasonOk}
            loading={mutation.isPending}
            loadingLabel={t("admin.busyDeleting")}
          >
            {t(keys.storeControls.forceDelete)}
          </Button>
        </>
      }
    >
      <form id={formId} style={{ display: "flex", flexDirection: "column", gap: 12 }} onSubmit={submit}>
        {error ? (
          <Banner tone="error" title={t("storeControls.deleteFailedTitle")}>
            {error} {t("admin.nothingChanged")}
          </Banner>
        ) : null}
        <Banner tone="warning">
          <span data-testid="force-delete-warning">{t(keys.storeControls.forceDeleteWarning)}</span>
        </Banner>
        {erased.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="rd-field-label">{t(keys.storeControls.forceDeleteWillErase)}</span>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }} data-testid="force-delete-counts">
              {erased.map((key) => (
                <li key={key} style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <span>{t(keys.storeControls.count[key])}</span>
                  <span className="rd-num">{counts[key]}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <Field label={t(keys.storeControls.deleteConfirmLabel, { slug: storeSlug })}>
          <Input
            name="confirmSlug"
            dir="ltr"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            value={typed}
            placeholder={storeSlug}
            onChange={(e) => setTyped(e.target.value)}
          />
        </Field>
        <Field label={t(keys.storeControls.forceDeleteReason)}>
          <TextArea
            name="forceDeleteReason"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </form>
    </Modal>
  );
}
